import React, { useState, useContext, useEffect, useRef } from 'react';
import {
    Heart,
    MessageCircle,
    Share2,
    MoreVertical,
    Image as ImageIcon,
    Smile,
    Send,
    Search,
    Users,
    TrendingUp,
    Clock,
    Sparkles,
    BookOpen,
    Loader2,
    PenTool,
    X,
    User,
    Quote,
    Trash2,
    Edit2,
    Check,
    ChevronLeft,
    ChevronRight,
    ExternalLink,
    Palette,
    Plus,
    Upload,
    Eye
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Input } from './ui/input';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { toast } from 'sonner';

const CATEGORIES = [
    { id: 'all', label: 'All Posts', icon: Clock },
    { id: 'trending', label: 'Trending', icon: TrendingUp },
    { id: 'wip', label: 'Work in Progress', icon: PenTool },
    { id: 'promotion', label: 'Promotions', icon: Sparkles },
    { id: 'collab', label: 'Collabs', icon: Users },
    { id: 'showcase', label: 'Showcase', icon: Palette },
];

const ImageGrid = ({ images }) => {
    if (!images || images.length === 0) return null;

    if (images.length === 1) {
        return (
            <div className="rounded-xl overflow-hidden border border-gray-800">
                <img src={images[0]} alt="Post attachment" className="w-full object-cover max-h-[500px]" />
            </div>
        );
    }

    return (
        <div className={`grid gap-2 ${images.length === 2 ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-3'}`}>
            {images.map((img, idx) => (
                <div key={idx} className="aspect-square rounded-xl overflow-hidden border border-gray-800 relative group">
                    <img src={img} alt={`Attachment ${idx + 1}`} className="w-full h-full object-cover" />
                </div>
            ))}
        </div>
    );
};

// URL regex for detecting links in post content
const URL_REGEX = /(https?:\/\/[^\s<>"']+)/gi;

// Link Preview Component - fetches OG metadata for a URL
const LinkPreview = ({ url, API }) => {
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const fetchMeta = async () => {
            try {
                const res = await axios.get(`${API}/og-metadata`, { params: { url } });
                if (!cancelled) setMeta(res.data);
            } catch {
                if (!cancelled) setError(true);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        fetchMeta();
        return () => { cancelled = true; };
    }, [url, API]);

    if (loading) return (
        <div className="rounded-xl border border-gray-700/50 bg-gray-800/30 p-3 mt-2 animate-pulse">
            <div className="flex gap-3">
                <div className="w-20 h-20 rounded-lg bg-gray-700/50 flex-shrink-0" />
                <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-700/50 rounded w-3/4" />
                    <div className="h-3 bg-gray-700/50 rounded w-full" />
                    <div className="h-3 bg-gray-700/50 rounded w-1/2" />
                </div>
            </div>
        </div>
    );

    if (error || !meta || (!meta.title && !meta.description)) return null;

    return (
        <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="block rounded-xl border border-gray-700/50 bg-gray-800/30 hover:bg-gray-800/60 hover:border-purple-500/30 transition-all duration-200 overflow-hidden mt-2 group"
        >
            <div className="flex">
                {meta.image && (
                    <div className="w-28 sm:w-36 flex-shrink-0">
                        <img src={meta.image} alt="" className="w-full h-full object-cover min-h-[80px] max-h-[120px]" />
                    </div>
                )}
                <div className="flex-1 p-3 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                        {meta.favicon && <img src={meta.favicon} alt="" className="w-4 h-4 rounded-sm" />}
                        <span className="text-xs text-gray-500 truncate">{meta.domain}</span>
                        <ExternalLink className="w-3 h-3 text-gray-600 group-hover:text-purple-400 transition-colors ml-auto flex-shrink-0" />
                    </div>
                    {meta.title && (
                        <h4 className="text-sm font-semibold text-white line-clamp-1 group-hover:text-purple-300 transition-colors">{meta.title}</h4>
                    )}
                    {meta.description && (
                        <p className="text-xs text-gray-400 line-clamp-2 mt-0.5">{meta.description}</p>
                    )}
                </div>
            </div>
        </a>
    );
};

// Render post content with clickable links
const renderContent = (content, API) => {
    if (!content) return null;

    const parts = content.split(URL_REGEX);
    const urls = content.match(URL_REGEX) || [];

    return (
        <>
            <p className="text-gray-200 whitespace-pre-wrap">
                {parts.map((part, i) => {
                    if (URL_REGEX.test(part)) {
                        // Reset regex lastIndex since we used 'g' flag
                        URL_REGEX.lastIndex = 0;
                        return (
                            <a
                                key={i}
                                href={part}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-purple-400 hover:text-purple-300 hover:underline break-all inline-flex items-center gap-1"
                            >
                                {part.length > 60 ? part.substring(0, 57) + '...' : part}
                                <ExternalLink className="w-3 h-3 inline flex-shrink-0" />
                            </a>
                        );
                    }
                    URL_REGEX.lastIndex = 0;
                    return <React.Fragment key={i}>{part}</React.Fragment>;
                })}
            </p>
            {/* Show link preview for the first URL found */}
            {urls.length > 0 && <LinkPreview url={urls[0]} API={API} />}
        </>
    );
};

// Individual Post Component

// ── Showcase Gallery Component ──
const ShowcaseGallery = ({ user, token, API }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showUpload, setShowUpload] = useState(false);
    const [uploadTitle, setUploadTitle] = useState('');
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadPreview, setUploadPreview] = useState('');
    const [uploading, setUploading] = useState(false);
    const [lightbox, setLightbox] = useState(null);
    const uploadRef = useRef(null);

    useEffect(() => {
        fetchGallery();
    }, []);

    const fetchGallery = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/community/gallery`);
            let gallery = res.data.items || [];
            // Mark liked items for current user
            if (user) {
                gallery = gallery.map(item => ({
                    ...item,
                    isLiked: (item.liked_by || []).includes(user.id)
                }));
            }
            setItems(gallery);
        } catch (err) {
            console.error('Error fetching gallery:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) {
            toast.error('Image must be under 10MB');
            return;
        }
        setUploadFile(file);
        setUploadPreview(URL.createObjectURL(file));
    };

    const handleUpload = async () => {
        if (!uploadFile) return;
        setUploading(true);
        try {
            // Upload image to Cloudinary
            const formData = new FormData();
            formData.append('file', uploadFile);
            const uploadRes = await axios.post(`${API}/upload/image`, formData, {
                headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` }
            });
            const imageUrl = uploadRes.data.secure_url || uploadRes.data.url;

            // Create gallery item
            const res = await axios.post(`${API}/community/gallery`, {
                image_url: imageUrl,
                title: uploadTitle.trim() || 'Untitled Artwork'
            }, { headers: { Authorization: `Bearer ${token}` } });

            setItems(prev => [{ ...res.data, isLiked: false }, ...prev]);
            setShowUpload(false);
            setUploadFile(null);
            setUploadPreview('');
            setUploadTitle('');
            toast.success('Artwork uploaded to Showcase!');
        } catch (err) {
            console.error('Upload error:', err);
            toast.error('Failed to upload artwork');
        } finally {
            setUploading(false);
        }
    };

    const handleLike = async (itemId) => {
        if (!user) { toast.error('Log in to like artworks'); return; }
        // Optimistic update
        setItems(prev => prev.map(item => {
            if (item.id === itemId) {
                return {
                    ...item,
                    isLiked: !item.isLiked,
                    likes_count: item.isLiked ? item.likes_count - 1 : item.likes_count + 1
                };
            }
            return item;
        }));
        try {
            await axios.post(`${API}/community/gallery/${itemId}/like`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
        } catch (err) {
            fetchGallery(); // Revert on error
        }
    };

    const handleDelete = async (itemId) => {
        if (!window.confirm('Delete this artwork from the Showcase?')) return;
        try {
            await axios.delete(`${API}/community/gallery/${itemId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setItems(prev => prev.filter(i => i.id !== itemId));
            toast.success('Artwork removed');
        } catch (err) {
            toast.error('Failed to delete');
        }
    };

    return (
        <div className="space-y-6">
            {/* Upload Button + Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400">
                        Showcase Gallery
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">Creator artworks & Comica masterpieces</p>
                </div>
                {user && (
                    <Button
                        className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white gap-2"
                        onClick={() => setShowUpload(true)}
                    >
                        <Plus className="w-4 h-4" /> Upload Artwork
                    </Button>
                )}
            </div>

            {/* Upload Modal */}
            {showUpload && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowUpload(false)}>
                    <Card className="bg-gray-900 border-gray-700 w-full max-w-md p-6 space-y-4" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-bold text-white">Upload Artwork</h3>
                            <Button variant="ghost" size="icon" onClick={() => setShowUpload(false)}>
                                <X className="w-5 h-5 text-gray-400" />
                            </Button>
                        </div>

                        {uploadPreview ? (
                            <div className="relative rounded-xl overflow-hidden border border-gray-700">
                                <img src={uploadPreview} alt="Preview" className="w-full max-h-64 object-contain bg-gray-950" />
                                <button
                                    onClick={() => { setUploadFile(null); setUploadPreview(''); }}
                                    className="absolute top-2 right-2 bg-gray-900/80 rounded-full p-1 text-white hover:bg-red-500 transition-colors"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        ) : (
                            <div
                                onClick={() => uploadRef.current?.click()}
                                className="border-2 border-dashed border-gray-700 hover:border-purple-500 rounded-xl p-10 text-center cursor-pointer transition-colors"
                            >
                                <Upload className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                                <p className="text-gray-400 text-sm">Click to select an image</p>
                                <p className="text-gray-600 text-xs mt-1">PNG, JPG up to 10MB</p>
                            </div>
                        )}
                        <input type="file" ref={uploadRef} onChange={handleFileSelect} className="hidden" accept="image/*" />

                        <Input
                            placeholder="Give your artwork a title..."
                            className="bg-gray-950/50 border-gray-700 text-white"
                            value={uploadTitle}
                            onChange={e => setUploadTitle(e.target.value)}
                            maxLength={100}
                        />

                        <Button
                            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
                            onClick={handleUpload}
                            disabled={!uploadFile || uploading}
                        >
                            {uploading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Uploading...</> : <><Upload className="w-4 h-4 mr-2" />Upload to Showcase</>}
                        </Button>
                    </Card>
                </div>
            )}

            {/* Lightbox */}
            {lightbox && (
                <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
                    <div className="relative max-w-4xl w-full" onClick={e => e.stopPropagation()}>
                        <img src={lightbox.image_url} alt={lightbox.title} className="w-full max-h-[80vh] object-contain rounded-xl" />
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 rounded-b-xl">
                            <h3 className="text-white font-bold text-lg">{lightbox.title}</h3>
                            <p className="text-gray-300 text-sm">by {lightbox.creator_name}</p>
                        </div>
                        <button onClick={() => setLightbox(null)} className="absolute top-3 right-3 bg-black/60 rounded-full p-2 text-white hover:bg-black/80">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            )}

            {/* Gallery Grid */}
            {loading ? (
                <div className="flex justify-center py-16">
                    <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
                </div>
            ) : items.length === 0 ? (
                <Card className="p-12 bg-gray-900/30 border-gray-800 text-center">
                    <Palette className="w-12 h-12 text-gray-700 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-400 mb-2">No artworks yet</h3>
                    <p className="text-gray-600 text-sm">Be the first to showcase your creation!</p>
                </Card>
            ) : (
                <div className="columns-2 sm:columns-3 gap-3 space-y-3">
                    {items.map(item => (
                        <div
                            key={item.id}
                            className="break-inside-avoid group relative rounded-xl overflow-hidden border border-gray-800 hover:border-purple-500/40 transition-all duration-300 cursor-pointer"
                        >
                            <img
                                src={item.image_url}
                                alt={item.title}
                                className="w-full object-cover"
                                onClick={() => setLightbox(item)}
                                loading="lazy"
                            />

                            {/* Overlay on hover */}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-3">
                                <h4 className="text-white font-semibold text-sm line-clamp-1">{item.title}</h4>
                                <div className="flex items-center justify-between mt-2">
                                    <div className="flex items-center gap-2">
                                        <Avatar className="w-5 h-5">
                                            <AvatarImage src={item.creator_avatar} />
                                            <AvatarFallback className="text-[8px]">{item.creator_name?.[0]}</AvatarFallback>
                                        </Avatar>
                                        <span className="text-gray-300 text-xs">{item.creator_name}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={(e) => { e.stopPropagation(); handleLike(item.id); }}
                                            className={`flex items-center gap-1 text-xs ${item.isLiked ? 'text-pink-500' : 'text-gray-400 hover:text-pink-400'} transition-colors`}
                                        >
                                            <Heart className={`w-4 h-4 ${item.isLiked ? 'fill-current' : ''}`} />
                                            {item.likes_count > 0 && item.likes_count}
                                        </button>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); setLightbox(item); }}
                                            className="text-gray-400 hover:text-white transition-colors"
                                        >
                                            <Eye className="w-4 h-4" />
                                        </button>
                                        {user && user.id === item.creator_id && (
                                            <button
                                                onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }}
                                                className="text-gray-400 hover:text-red-400 transition-colors"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Always-visible creator badge */}
                            <div className="absolute top-2 left-2 bg-black/60 backdrop-blur-sm rounded-full px-2 py-1 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 sm:opacity-100 transition-opacity">
                                <Avatar className="w-4 h-4">
                                    <AvatarImage src={item.creator_avatar} />
                                    <AvatarFallback className="text-[7px]">{item.creator_name?.[0]}</AvatarFallback>
                                </Avatar>
                                <span className="text-white text-[10px] font-medium">{item.creator_name}</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};


const PostCard = ({ post: initialPost, user, token, API, onLike, onDelete }) => {
    const navigate = useNavigate();
    const [post, setPost] = useState(initialPost);
    const [showComments, setShowComments] = useState(false);
    const [comments, setComments] = useState([]);
    const [loadingComments, setLoadingComments] = useState(false);
    const [newComment, setNewComment] = useState('');
    const [submittingComment, setSubmittingComment] = useState(false);

    // Edit Mode State
    const [isEditing, setIsEditing] = useState(false);
    const [editContent, setEditContent] = useState(initialPost.content);
    const [editImages, setEditImages] = useState(initialPost.images || (initialPost.image ? [initialPost.image] : []));
    const [newEditFiles, setNewEditFiles] = useState([]);
    const [newEditPreviews, setNewEditPreviews] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const editFileInputRef = useRef(null);

    useEffect(() => {
        setPost(initialPost);
        setEditContent(initialPost.content);
        setEditImages(initialPost.images || (initialPost.image ? [initialPost.image] : []));
        setNewEditFiles([]);
        setNewEditPreviews([]);
    }, [initialPost]);

    const handleReadComic = (comicId) => {
        if (comicId) navigate(`/comic/${comicId}`);
        else toast.info("No comic linked to this post");
    };

    const handleShare = () => {
        const shareText = `Check out this post by ${post.author_name} on Comica!`;
        const shareUrl = window.location.href;

        if (navigator.share) {
            navigator.share({ title: 'Comica Creator Hub', text: shareText, url: shareUrl }).catch(console.error);
        } else {
            navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
            toast.success("Link copied to clipboard!");
        }
    };

    const toggleComments = async () => {
        const newShowState = !showComments;
        setShowComments(newShowState);
        if (newShowState && comments.length === 0 && post.comments_count > 0) {
            setLoadingComments(true);
            try {
                const res = await axios.get(`${API}/community/posts/${post.id}/comments`);
                setComments(res.data);
            } catch (error) {
                console.error("Error fetching comments:", error);
            } finally {
                setLoadingComments(false);
            }
        }
    };

    const handleAddComment = async () => {
        if (!newComment.trim() || !user) return;
        setSubmittingComment(true);
        try {
            const res = await axios.post(`${API}/community/posts/${post.id}/comments`, { content: newComment }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setComments(prev => [res.data, ...prev]);
            setPost(prev => ({ ...prev, comments_count: prev.comments_count + 1 }));
            setNewComment('');
        } catch (error) {
            console.error("Error posting comment:", error);
            toast.error("Failed to post comment");
        } finally {
            setSubmittingComment(false);
        }
    };

    const handleEditFileSelect = (e) => {
        const files = Array.from(e.target.files);
        if (files.length > 0) {
            const validFiles = files.filter(f => f.size <= 5 * 1024 * 1024);
            if (validFiles.length < files.length) toast.error("Some files skipped (max 5MB each)");

            setNewEditFiles(prev => [...prev, ...validFiles]);
            const newUrls = validFiles.map(f => URL.createObjectURL(f));
            setNewEditPreviews(prev => [...prev, ...newUrls]);
        }
    };

    const removeNewEditImage = (index) => {
        setNewEditFiles(prev => prev.filter((_, i) => i !== index));
        setNewEditPreviews(prev => {
            const newUrls = [...prev];
            URL.revokeObjectURL(newUrls[index]);
            return newUrls.filter((_, i) => i !== index);
        });
        if (editFileInputRef.current) editFileInputRef.current.value = '';
    };

    const handleSaveEdit = async () => {
        setIsSaving(true);
        try {
            let finalImages = [...editImages];

            // Upload new images if any
            if (newEditFiles.length > 0) {
                const uploadPromises = newEditFiles.map(file => {
                    const formData = new FormData();
                    formData.append('file', file);
                    return axios.post(`${API}/upload/image`, formData, {
                        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` }
                    });
                });

                try {
                    const responses = await Promise.all(uploadPromises);
                    const uploadedUrls = responses.map(res => res.data.secure_url || res.data.url);
                    finalImages = [...finalImages, ...uploadedUrls];
                } catch (uploadError) {
                    console.error("Upload failed in edit:", uploadError);
                    toast.error("Failed to upload new images");
                    setIsSaving(false);
                    return;
                }
            }

            const res = await axios.put(`${API}/community/posts/${post.id}`, {
                content: editContent,
                images: finalImages
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setPost(res.data);
            setIsEditing(false);
            setNewEditFiles([]);
            setNewEditPreviews([]);
            toast.success("Post updated!");
        } catch (error) {
            console.error("Error updating post:", error);
            toast.error("Failed to update post");
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm("Are you sure you want to delete this post?")) return;
        try {
            await axios.delete(`${API}/community/posts/${post.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            onDelete(post.id); // Notify parent to remove from list
            toast.success("Post deleted");
        } catch (error) {
            console.error("Error deleting post:", error);
            toast.error("Failed to delete post");
        }
    };

    return (
        <Card className="bg-gray-900/50 border-gray-800 backdrop-blur-sm overflow-hidden">
            <div className="p-4">
                {/* Post Header */}
                <div className="flex justify-between items-start mb-4">
                    <div className="flex gap-3">
                        <Avatar>
                            <AvatarImage src={post.author_avatar} />
                            <AvatarFallback>{post.author_name?.[0]}</AvatarFallback>
                        </Avatar>
                        <div>
                            <div className="flex items-center gap-2">
                                <span className="font-semibold text-white hover:underline cursor-pointer">
                                    {post.author_name}
                                </span>
                                <span className="text-gray-500 text-sm">{post.author_handle}</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                                {new Date(post.created_at).toLocaleDateString()}
                                <span>•</span>
                                <span className="text-purple-400 uppercase text-[10px] tracking-wider border border-purple-500/30 px-1.5 py-0.5 rounded">
                                    {post.category}
                                </span>
                            </div>
                        </div>
                    </div>

                    {user && user.id === post.author_id && (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="text-gray-500">
                                    <MoreVertical className="w-5 h-5" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="bg-gray-900 border-gray-800 text-gray-300">
                                <DropdownMenuItem onClick={() => setIsEditing(true)}>
                                    <Edit2 className="w-4 h-4 mr-2" /> Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={handleDelete} className="text-red-500 focus:text-red-400">
                                    <Trash2 className="w-4 h-4 mr-2" /> Delete
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                </div>

                {/* Post Content & Edit Mode */}
                {isEditing ? (
                    <div className="space-y-4">
                        <textarea
                            className="w-full bg-gray-950/50 border border-gray-700 rounded-lg p-3 text-white focus:outline-none focus:border-purple-500"
                            value={editContent}
                            onChange={(e) => setEditContent(e.target.value)}
                            rows={4}
                        />

                        {/* Edit Images Section */}
                        <div className="space-y-2">
                            <div className="flex flex-wrap gap-2">
                                {/* Existing Images */}
                                {editImages.map((img, idx) => (
                                    <div key={`existing-${idx}`} className="relative w-20 h-20 flex-shrink-0">
                                        <img src={img} className="w-full h-full object-cover rounded-md border border-gray-700" alt="Existing" />
                                        <button
                                            onClick={() => setEditImages(prev => prev.filter((_, i) => i !== idx))}
                                            className="absolute -top-1 -right-1 bg-gray-900/80 rounded-full p-0.5 text-white hover:bg-red-500 transition-colors"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </div>
                                ))}

                                {/* New Images Previews */}
                                {newEditPreviews.map((url, idx) => (
                                    <div key={`new-${idx}`} className="relative w-20 h-20 flex-shrink-0">
                                        <img src={url} className="w-full h-full object-cover rounded-md border border-green-500/50" alt="New Upload" />
                                        <button
                                            onClick={() => removeNewEditImage(idx)}
                                            className="absolute -top-1 -right-1 bg-gray-900/80 rounded-full p-0.5 text-white hover:bg-red-500 transition-colors"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </div>
                                ))}

                                {/* Add Image Button */}
                                <div className="w-20 h-20 flex items-center justify-center border-2 border-dashed border-gray-700 rounded-md hover:border-purple-500 cursor-pointer" onClick={() => editFileInputRef.current?.click()}>
                                    <ImageIcon className="w-6 h-6 text-gray-500" />
                                </div>
                                <input
                                    type="file"
                                    ref={editFileInputRef}
                                    onChange={handleEditFileSelect}
                                    className="hidden"
                                    accept="image/*"
                                    multiple
                                />
                            </div>
                        </div>

                        <div className="flex gap-2 justify-end">
                            <Button variant="ghost" size="sm" onClick={() => setIsEditing(false)}>Cancel</Button>
                            <Button size="sm" className="bg-purple-600 hover:bg-purple-700" onClick={handleSaveEdit} disabled={isSaving}>
                                {isSaving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Check className="w-3 h-3 mr-1" />}
                                Save Changes
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {renderContent(post.content, API)}

                        {/* Display Images */}
                        <ImageGrid images={post.images || (post.image ? [post.image] : [])} />

                        <div className="flex flex-wrap gap-2">
                            {post.tags.map(tag => (
                                <span key={tag} className="text-blue-400 hover:underline text-sm cursor-pointer">#{tag}</span>
                            ))}
                        </div>
                    </div>
                )}

                <Separator className="my-4 bg-gray-800" />

                {/* Actions */}
                <div className="flex justify-between text-gray-400">
                    <div className="flex gap-4">
                        <Button
                            variant="ghost"
                            className={`gap-2 ${post.isLiked ? 'text-pink-500' : 'hover:text-pink-500 hover:bg-pink-500/10'}`}
                            onClick={() => onLike(post.id, post.isLiked)}
                        >
                            <Heart className={`w-5 h-5 ${post.isLiked ? 'fill-current' : ''}`} />
                            <span className="text-sm">{post.likes_count}</span>
                        </Button>
                        <Button
                            variant="ghost"
                            className={`gap-2 ${showComments ? 'text-blue-500 bg-blue-500/10' : 'hover:text-blue-500 hover:bg-blue-500/10'}`}
                            onClick={toggleComments}
                        >
                            <MessageCircle className="w-5 h-5" />
                            <span className="text-sm">{post.comments_count}</span>
                        </Button>
                        <Button variant="ghost" className="hover:text-green-500 hover:bg-green-500/10 gap-2" onClick={handleShare}>
                            <Share2 className="w-5 h-5" />
                        </Button>
                    </div>
                    {post.comic_id && (
                        <Button className="bg-purple-600/20 text-purple-300 hover:bg-purple-600/40 border border-purple-500/50 gap-2 h-9" onClick={() => handleReadComic(post.comic_id)}>
                            <BookOpen className="w-4 h-4" /><span className="text-sm font-medium">Read Comic</span>
                        </Button>
                    )}
                </div>

                {/* Comments Section */}
                {showComments && (
                    <div className="mt-4 pt-4 border-t border-gray-800 space-y-4">
                        {user ? (
                            <div className="flex gap-3">
                                <Avatar className="w-8 h-8">
                                    <AvatarImage src={user.avatar} />
                                    <AvatarFallback>{user.username?.[0]}</AvatarFallback>
                                </Avatar>
                                <div className="flex-1 flex gap-2">
                                    <Input
                                        placeholder="Write a comment..."
                                        className="bg-gray-950/50 border-gray-800 h-9"
                                        value={newComment}
                                        onChange={(e) => setNewComment(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && handleAddComment()}
                                    />
                                    <Button size="icon" className="h-9 w-9 bg-purple-600 hover:bg-purple-700" onClick={handleAddComment} disabled={submittingComment || !newComment.trim()}>
                                        {submittingComment ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center text-sm text-gray-500 py-2">Log in to comment</div>
                        )}
                        <div className="space-y-4">
                            {loadingComments ? (
                                <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 text-gray-500 animate-spin" /></div>
                            ) : comments.length > 0 ? (
                                comments.map(comment => (
                                    <div key={comment.id} className="flex gap-3">
                                        <Avatar className="w-8 h-8">
                                            <AvatarImage src={comment.author_avatar} />
                                            <AvatarFallback>{comment.author_name?.[0]}</AvatarFallback>
                                        </Avatar>
                                        <div>
                                            <div className="flex items-baseline gap-2">
                                                <span className="text-sm font-semibold text-white">{comment.author_name}</span>
                                                <span className="text-xs text-gray-500">{new Date(comment.created_at).toLocaleDateString()}</span>
                                            </div>
                                            <p className="text-sm text-gray-300">{comment.content}</p>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="text-center text-sm text-gray-500 py-2">No comments yet.</div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </Card>
    );
};

const Community = () => {
    const { user, token, API } = useContext(AuthContext);
    const navigate = useNavigate();
    const [activeCategory, setActiveCategory] = useState('all');
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newPostContent, setNewPostContent] = useState('');
    const [isPosting, setIsPosting] = useState(false);

    // Multi-Image Upload State
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [previewUrls, setPreviewUrls] = useState([]);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    // Quote State
    const [quote, setQuote] = useState(null);
    const [isEditingQuote, setIsEditingQuote] = useState(false);
    const [newQuoteText, setNewQuoteText] = useState('');

    // Active Users (Realtime)
    const [activeUsers, setActiveUsers] = useState([]);

    useEffect(() => {
        fetchPosts();
        fetchQuote();

        // WebSocket Connection for Realtime Active Users
        let ws = null;
        if (token) {
            // Determine WS URL (ws: for http, wss: for https)
            const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
            // Assuming API URL is http://localhost:8000, we need ws://localhost:8000/ws/community
            // If API var ends with /api, strip it
            const apiBase = API.replace('/api', '').replace('http:', protocol).replace('https:', protocol);
            const wsUrl = `${apiBase}/ws/community?token=${token}`;

            console.log("Connecting to WS:", wsUrl);

            ws = new WebSocket(wsUrl);

            ws.onopen = () => {
                console.log("Connected to Community WebSocket");
            };

            ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    if (data.type === 'active_users') {
                        // Filter out self if desired, but seeing yourself is good confirmation
                        // Ensure unique list
                        setActiveUsers(data.users);
                    }
                } catch (e) {
                    console.error("WS Message Error:", e);
                }
            };

            ws.onclose = () => {
                console.log("Disconnected from Community WebSocket");
            };

            ws.onerror = (e) => {
                console.error("WS Error:", e);
            };
        }

        return () => {
            if (ws) ws.close();
        };
    }, [activeCategory, token]);

    const fetchQuote = async () => {
        try {
            const res = await axios.get(`${API}/community/quote`);
            setQuote(res.data);
        } catch (error) { console.error("Error fetching quote:", error); }
    };

    const handleSetQuote = async () => {
        if (!newQuoteText.trim()) return;
        try {
            const res = await axios.post(`${API}/community/quote`, { content: newQuoteText }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setQuote(res.data);
            setIsEditingQuote(false);
            setNewQuoteText('');
            toast.success("Community quote updated!");
        } catch (error) {
            console.error("Error setting quote:", error);
            toast.error("Failed to update quote");
        }
    };

    const fetchPosts = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API}/community/posts`, { params: { category: activeCategory } });
            let fetchedPosts = response.data;
            if (user && token) {
                const postsWithLikes = await Promise.all(fetchedPosts.map(async (post) => {
                    try {
                        const likeRes = await axios.get(`${API}/community/posts/${post.id}/like-status`, { headers: { Authorization: `Bearer ${token}` } });
                        return { ...post, isLiked: likeRes.data.liked };
                    } catch (e) { return { ...post, isLiked: false }; }
                }));
                setPosts(postsWithLikes);
            } else {
                setPosts(fetchedPosts);
            }
        } catch (error) {
            console.error("Error fetching posts:", error);
            toast.error("Failed to load community feed");
        } finally {
            setLoading(false);
        }
    };

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        if (files.length > 0) {
            const validFiles = files.filter(f => f.size <= 5 * 1024 * 1024);
            if (validFiles.length < files.length) toast.error("Some files skipped (max 5MB each)");

            // Append new files
            const newFiles = [...selectedFiles, ...validFiles].slice(0, 4); // Limit to 4 images max for now
            setSelectedFiles(newFiles);

            // Create previews
            const newUrls = validFiles.map(f => URL.createObjectURL(f));
            setPreviewUrls(prev => [...prev, ...newUrls].slice(0, 4));
        }
    };

    const removeImage = (index) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
        setPreviewUrls(prev => {
            const newUrls = [...prev];
            URL.revokeObjectURL(newUrls[index]);
            return newUrls.filter((_, i) => i !== index);
        });
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleCreatePost = async () => {
        if (!newPostContent.trim() && selectedFiles.length === 0) return;

        setIsPosting(true);
        try {
            let imageUrls = [];

            // Upload images
            if (selectedFiles.length > 0) {
                setUploading(true);
                // Upload sequentially or parallel
                const uploadPromises = selectedFiles.map(file => {
                    const formData = new FormData();
                    formData.append('file', file);
                    return axios.post(`${API}/upload/image`, formData, {
                        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` }
                    });
                });

                try {
                    const responses = await Promise.all(uploadPromises);
                    imageUrls = responses.map(res => res.data.secure_url || res.data.url);
                } catch (uploadError) {
                    console.error("Upload failed:", uploadError);
                    toast.error("Failed to upload some images");
                    setIsPosting(false);
                    setUploading(false);
                    return;
                }
                setUploading(false);
            }

            // Create Post
            await axios.post(`${API}/community/posts`, {
                content: newPostContent,
                category: activeCategory === 'all' || activeCategory === 'trending' ? 'general' : activeCategory,
                images: imageUrls, // Send list of images
                tags: []
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setNewPostContent('');
            setSelectedFiles([]);
            setPreviewUrls([]);
            toast.success("Post created!");
            fetchPosts();
        } catch (error) {
            console.error("Error creating post:", error);
            toast.error("Failed to create post");
        } finally {
            setIsPosting(false);
            setUploading(false);
        }
    };

    const handlePostDelete = (deletedPostId) => {
        setPosts(prev => prev.filter(p => p.id !== deletedPostId));
    };

    const handleLike = async (postId, currentLikedStatus) => {
        if (!user) { toast.error("Please log in to like posts"); return; }
        setPosts(prev => prev.map(p => {
            if (p.id === postId) {
                return { ...p, isLiked: !p.isLiked, likes_count: p.isLiked ? p.likes_count - 1 : p.likes_count + 1 };
            }
            return p;
        }));
        try {
            await axios.post(`${API}/community/posts/${postId}/like`, {}, { headers: { Authorization: `Bearer ${token}` } });
        } catch (error) {
            console.error("Error liking post:", error);
            toast.error("Failed to like post");
            fetchPosts();
        }
    };

    return (
        <div className="min-h-screen bg-gray-950 pt-20 pb-10 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-6">

                {/* Left Sidebar */}
                <div className="lg:col-span-3 space-y-6 lg:sticky lg:top-24 lg:h-fit">
                    <Card className="p-4 bg-gray-900/50 border-gray-800 backdrop-blur-sm">
                        <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-cyan-400 mb-4 px-2">
                            Creator Hub
                        </h2>
                        <div className="space-y-1">
                            {CATEGORIES.map((cat) => (
                                <Button
                                    key={cat.id}
                                    variant="ghost"
                                    className={`w-full justify-start text-sm font-medium ${activeCategory === cat.id ? "bg-purple-500/10 text-purple-400" : "text-gray-400 hover:text-white"}`}
                                    onClick={() => setActiveCategory(cat.id)}
                                >
                                    <cat.icon className="w-4 h-4 mr-3" /> {cat.label}
                                </Button>
                            ))}
                        </div>
                    </Card>
                    <Card className="p-4 bg-gray-900/50 border-gray-800 backdrop-blur-sm">
                        <h3 className="text-sm font-semibold text-gray-300 mb-3 px-2">Popular Tags</h3>
                        <div className="flex flex-wrap gap-2">
                            {['#MangaArt', '#Webtoon', '#Storytelling', '#DigitalArt', '#ComicCon'].map(tag => (
                                <Badge key={tag} variant="secondary" className="bg-gray-800 hover:bg-gray-700 text-gray-400 cursor-pointer">{tag}</Badge>
                            ))}
                        </div>
                    </Card>
                </div>

                {/* Main Feed */}
                <div className="lg:col-span-6 space-y-6">
                    {activeCategory === 'showcase' ? (
                        <ShowcaseGallery user={user} token={token} API={API} />
                    ) : (
                        <>
                            {user ? (
                                <Card className="p-4 bg-gray-900/50 border-gray-800 backdrop-blur-sm">
                                    <div className="flex gap-4">
                                        <Avatar className="w-10 h-10">
                                            <AvatarImage src={user.avatar} />
                                            <AvatarFallback>{user.username?.[0]}</AvatarFallback>
                                        </Avatar>
                                        <div className="flex-1 space-y-4">
                                            <Input
                                                placeholder="What's on your creative mind?"
                                                className="bg-gray-950/50 border-gray-800 text-white placeholder:text-gray-500"
                                                value={newPostContent}
                                                onChange={(e) => setNewPostContent(e.target.value)}
                                                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleCreatePost()}
                                            />

                                            {/* Multi-Image Preview */}
                                            {previewUrls.length > 0 && (
                                                <div className="flex gap-2 overflow-x-auto pb-2">
                                                    {previewUrls.map((url, idx) => (
                                                        <div key={idx} className="relative w-24 h-24 flex-shrink-0">
                                                            <img src={url} alt={`Preview ${idx}`} className="w-full h-full object-cover rounded-lg border border-gray-700" />
                                                            <button onClick={() => removeImage(idx)} className="absolute -top-2 -right-2 bg-gray-800 rounded-full p-1 text-gray-400 hover:text-white border border-gray-600">
                                                                <X className="w-3 h-3" />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            <div className="flex justify-between items-center">
                                                <div className="flex gap-2 text-gray-400">
                                                    <input type="file" ref={fileInputRef} onChange={handleFileSelect} className="hidden" accept="image/*" multiple />
                                                    <Button variant="ghost" size="icon" className="hover:text-purple-400" onClick={() => fileInputRef.current?.click()} disabled={uploading || isPosting}>
                                                        <ImageIcon className="w-5 h-5" />
                                                    </Button>
                                                    <Button variant="ghost" size="icon" className="hover:text-purple-400"><Smile className="w-5 h-5" /></Button>
                                                </div>
                                                <Button className="bg-purple-600 hover:bg-purple-700 text-white" onClick={handleCreatePost} disabled={isPosting || (!newPostContent.trim() && selectedFiles.length === 0)}>
                                                    {isPosting ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />{uploading ? "Uploading..." : "Posting..."}</> : <><Send className="w-4 h-4 mr-2" />Post</>}
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </Card>
                            ) : (
                                <Card className="p-6 bg-gradient-to-r from-purple-900/40 to-blue-900/40 border-purple-500/30 backdrop-blur-sm text-center">
                                    <h3 className="text-lg font-semibold text-white mb-2">Join the Conversation</h3>
                                    <p className="text-gray-400 mb-4">Log in to share your art, get feedback, and connect with other creators.</p>
                                    <Button onClick={() => navigate('/login')} className="bg-white text-black hover:bg-gray-200">Log In / Sign Up</Button>
                                </Card>
                            )}

                            <div className="space-y-6">
                                {loading ? (
                                    <div className="flex justify-center py-10"><Loader2 className="w-8 h-8 text-purple-500 animate-spin" /></div>
                                ) : posts.length === 0 ? (
                                    <div className="text-center py-10 text-gray-500">No posts yet. Be the first to share!</div>
                                ) : (
                                    posts.map((post) => (
                                        <PostCard key={post.id} post={post} user={user} token={token} API={API} onLike={handleLike} onDelete={handlePostDelete} />
                                    ))
                                )}
                            </div>
                        </>
                    )}
                </div>

                {/* Right Sidebar */}
                <div className="lg:col-span-3 space-y-6 lg:sticky lg:top-24 lg:h-fit">
                    <Card className="p-4 bg-gradient-to-br from-purple-900/20 to-blue-900/20 border-purple-500/20 backdrop-blur-sm">
                        <div className="flex items-center gap-2 mb-4">
                            <Quote className="w-5 h-5 text-purple-400" />
                            <h3 className="font-bold text-white">Community Quote</h3>
                        </div>
                        {quote && !isEditingQuote ? (
                            <div className="space-y-4">
                                <div className="relative">
                                    <Quote className="w-8 h-8 text-purple-500/20 absolute -top-2 -left-2" />
                                    <p className="text-gray-200 italic relative z-10 pl-6">"{quote.content}"</p>
                                </div>
                                <div className="flex gap-3 items-center justify-end">
                                    <div className="text-right">
                                        <div className="font-semibold text-white text-sm">- {quote.author_name}</div>
                                        <div className="text-xs text-gray-500">{quote.created_at && new Date(quote.created_at).toLocaleDateString()}</div>
                                    </div>
                                    <Avatar className="w-8 h-8 border border-purple-500/30">
                                        <AvatarImage src={quote.author_avatar} />
                                        <AvatarFallback>{quote.author_name?.[0]}</AvatarFallback>
                                    </Avatar>
                                </div>
                                {user && (
                                    <Button variant="ghost" className="w-full text-xs text-purple-400 hover:text-purple-300 hover:bg-purple-500/10 h-8" onClick={() => setIsEditingQuote(true)}>Set New Quote</Button>
                                )}
                            </div>
                        ) : isEditingQuote ? (
                            <div className="space-y-3">
                                <p className="text-xs text-gray-400">Set a new quote for the whole community to see!</p>
                                <textarea className="w-full bg-gray-950/50 border border-gray-700 rounded-md p-2 text-sm text-white focus:outline-none focus:border-purple-500 resize-none h-24" placeholder="Enter quote here..." value={newQuoteText} onChange={(e) => setNewQuoteText(e.target.value)} maxLength={150} />
                                <div className="flex gap-2">
                                    <Button variant="ghost" className="flex-1 text-gray-400 hover:text-white" onClick={() => setIsEditingQuote(false)}>Cancel</Button>
                                    <Button className="flex-1 bg-purple-600 hover:bg-purple-700 text-white" onClick={handleSetQuote} disabled={!newQuoteText.trim()}>Post Quote</Button>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-4 text-gray-500">
                                <p className="text-sm mb-2">No active quote.</p>
                                {user && <Button variant="outline" className="text-purple-400 border-purple-500/30 hover:bg-purple-500/10" onClick={() => setIsEditingQuote(true)}>Set One</Button>}
                            </div>
                        )}
                    </Card>

                    {/* Active Users List (Realtime) */}
                    <Card className="p-4 bg-gray-900/50 border-gray-800 backdrop-blur-sm">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-semibold text-gray-300">Active Now</h3>
                            <span className="flex h-2 w-2 rounded-full bg-green-500 animate-pulse"></span>
                        </div>
                        <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                            {activeUsers.length === 0 ? (
                                <div className="text-center text-xs text-gray-500 py-4">No one else is online right now.</div>
                            ) : (
                                activeUsers.map(u => (
                                    <div key={u.id} className="flex items-center justify-between group">
                                        <div className="flex items-center gap-2">
                                            <div className="relative">
                                                <Avatar className="w-8 h-8 border border-gray-700">
                                                    <AvatarImage src={u.avatar} />
                                                    <AvatarFallback>{u.username?.[0]}</AvatarFallback>
                                                </Avatar>
                                                <span className="absolute bottom-0 right-0 block h-2 w-2 rounded-full ring-2 ring-gray-900 bg-green-500"></span>
                                            </div>
                                            <div className="flex flex-col">
                                                <span className="text-sm text-gray-200 font-medium group-hover:text-purple-400 transition-colors cursor-pointer text-ellipsis overflow-hidden max-w-[120px] whitespace-nowrap" title={u.display_name}>
                                                    {u.display_name}
                                                </span>
                                                <span className="text-[10px] text-gray-500">@{u.username}</span>
                                            </div>
                                        </div>
                                        {/* Optional: Add Follow/Chat button here later */}
                                    </div>
                                ))
                            )}
                        </div>
                    </Card>
                </div>

            </div>
        </div>
    );
};

export default Community;
